"use client";

import { ReactNode, useEffect } from "react";
import { SessionProvider } from "next-auth/react";
import { AuthProvider } from "@/hooks/use-auth";
import { BrandSwitchProvider } from "@/contexts/brand-switch-context";
import { SessionActivityProvider } from "@/contexts/session-activity-context";
import { DevModeProvider } from "@/contexts/dev-mode-context";
import { SetupPhaseProvider } from "@/contexts/setup-phase-context";
import { LockScreenModal } from "@/components/auth/LockScreenModal";
import { EmailVerificationModal } from "@/components/auth/EmailVerificationModal";
import { DevModeToggle } from "@/components/ui/DevModeToggle";
import { WebBrowserOverlay } from "@/components/web-browser";
import type { AuthState } from "@/lib/auth-types";

interface ProvidersProps {
	children: ReactNode;
	authState?: AuthState | null;
	allowClientDevAuth?: boolean;
}

function DevRuntimeReset() {
	useEffect(() => {
		if (process.env.NODE_ENV !== "development") {
			return;
		}

		let isCancelled = false;

		const resetDevRuntimeState = async () => {
			const browserWindow = window as Window & {
				caches?: CacheStorage;
			};

			const hasSessionStorage = typeof browserWindow.sessionStorage !== "undefined";
			const alreadyReloaded = hasSessionStorage
				? browserWindow.sessionStorage.getItem("vastipost-dev-runtime-reset") === "1"
				: false;

			const registrations = "serviceWorker" in navigator
				? await navigator.serviceWorker.getRegistrations()
				: [];
			const cacheKeys = browserWindow.caches ? await browserWindow.caches.keys() : [];

			if (isCancelled) {
				return;
			}

			if (!registrations.length && !cacheKeys.length) {
				if (hasSessionStorage) {
					browserWindow.sessionStorage.removeItem("vastipost-dev-runtime-reset");
				}
				return;
			}

			await Promise.all(registrations.map((registration) => registration.unregister()));
			await Promise.all(cacheKeys.map((cacheKey) => browserWindow.caches?.delete(cacheKey) ?? false));

			if (isCancelled || alreadyReloaded || !hasSessionStorage) {
				return;
			}

			browserWindow.sessionStorage.setItem("vastipost-dev-runtime-reset", "1");
			browserWindow.location.reload();
		};

		void resetDevRuntimeState();

		return () => {
			isCancelled = true;
		};
	}, []);

	return null;
}

export function Providers({ children, authState, allowClientDevAuth = false }: ProvidersProps) {
	return (
		<SessionProvider>
			<DevRuntimeReset />
			<AuthProvider initialState={authState} allowDevModeFallback={allowClientDevAuth}>
				<DevModeProvider>
					<SetupPhaseProvider>
						<BrandSwitchProvider>
							<SessionActivityProvider>
								{children}
								<LockScreenModal />
								<EmailVerificationModal />
								<DevModeToggle />
								<WebBrowserOverlay />
							</SessionActivityProvider>
						</BrandSwitchProvider>
					</SetupPhaseProvider>
				</DevModeProvider>
			</AuthProvider>
		</SessionProvider>
	);
}
